import React, { useEffect, useMemo, useState } from 'react';
import { FaArrowRight } from 'react-icons/fa6';
import { useNavigate } from 'react-router-dom';

import { MedsusPublicHeader } from './MedsusPublicHeader';
import {
  PageWrapper,
  InfoBanner,
  BannerText,
  TableContainer,
  LoadingContainer,
  ErrorMessage,
  ErrorText,
  RetryButton,
  NavigationIconButton,
  LoadingSpinner,
} from './styles';
import CustomTable, {
  CustomTableHeader,
} from '../../../components/CustomTable';
import { IProdutoVmp } from '../../../modules/types/Medsus';
import { getAllMedicamentosByDiretrizesAtivasPublic } from '../../../services/medsus/medsusItemService';
import { MedsusPublicTableRow } from '../../daf/ProdutosDAF/MedSUS/auxliarProdutoMedsus';

export function MedsusPublicView() {
  const navigate = useNavigate();
  const [data, setData] = useState<IProdutoVmp[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);
      const registros = await getAllMedicamentosByDiretrizesAtivasPublic();
      setData(registros);
    } catch (err) {
      setError('Erro ao carregar dados. Tente novamente.');
      console.error('Erro ao obter dados:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const tableData: MedsusPublicTableRow[] = useMemo(() => {
    return (
      data?.map((item: IProdutoVmp) => {
        const localizacao = (() => {
          if (!item.diretrizes || item.diretrizes.length === 0) {
            return 'Nacional';
          }
          const localizacoes = item.diretrizes
            .filter((diretriz) => diretriz.ses)
            .map(
              (diretriz) =>
                `${diretriz.ses.municipioIbge.ufSigla} - ${diretriz.ses.municipioIbge.municipio}`,
            );
          return localizacoes.length > 0 ? localizacoes.join(', ') : 'Nacional';
        })();

        return {
          nome: item.nome ?? 'Não informado',
          localizacao,
          id: Number(item.id),
        };
      }) || []
    );
  }, [data]);

  const headersMedsusPublic: CustomTableHeader<MedsusPublicTableRow>[] = [
    {
      text: 'Nome do Medicamento',
      key: 'nome',
      width: 70,
    },
    {
      text: 'UF - Município',
      key: 'localizacao',
      width: 25,
    },
    {
      text: '',
      key: 'id',
      width: 5,
      renderable: false,
      renderCell: (_, row) => (
        <NavigationIconButton
          onClick={() => navigate(`/medsus/${row.id}`)}
          aria-label="Ver detalhes"
          title="Ver detalhes"
        >
          <FaArrowRight />
        </NavigationIconButton>
      ),
    },
  ];

  const handleRetry = () => {
    fetchData();
  };

  return (
    <PageWrapper>
      <MedsusPublicHeader />

      <InfoBanner>
        <BannerText>
          Seu portal para acesso a informações de medicamentos, centros de
          dispensação e estoques pertos de você.
        </BannerText>
      </InfoBanner>

      {loading ? (
        <TableContainer>
          <LoadingContainer>
            <LoadingSpinner />
            <p>Carregando dados...</p>
          </LoadingContainer>
        </TableContainer>
      ) : error ? (
        <TableContainer>
          <ErrorMessage>
            <ErrorText>{error}</ErrorText>
            <RetryButton onClick={handleRetry}>Tentar novamente</RetryButton>
          </ErrorMessage>
        </TableContainer>
      ) : (
        <TableContainer>
          <CustomTable
            headers={headersMedsusPublic}
            data={tableData}
            filterable
            showPagination
            itemsPerPage={10}
            emptyMessage="Nenhuma diretriz clínica encontrada"
          />
        </TableContainer>
      )}
    </PageWrapper>
  );
}
